import React, { useState } from 'react';
import styled from 'styled-components';
import GeneralPopup from '../GeneralPopup/index.jsx';
import Loader from '../Loader';
import {
  QuestionCounter,
  QuestionBtn,
  QuestionBox,
} from '../FeedbacksComponents/FeedbackQuestionSlider/index.jsx';
import LeftIcon from '../../static/img/darkgray16leftarrow.svg';
import RightIcon from '../../static/img/darkgray16rightarrow.svg';
import { useAssignmentStatusById, useAssignmentById } from '../state/hooks.js';
import RubricIcon from '../../static/img/rubric-outcome.svg';
import { Tooltip } from '@mui/material';
import ViewSubmissionsButton from '../ViewTaskSubmissionButton/index.jsx';
import CloseIcon from '../../static/img/close.svg';
import { htmlToPlainText } from '../BulkUpload/utils/html';

// Question titles are stored as Quill HTML (e.g. "<p>Operations</p>"). The
// popup shows a title as one truncated line plus a tooltip, so it is reduced
// to plain text (tags stripped, entities decoded) and never injected as HTML
// (JED-1282).
const toPlainTitle = (value) =>
  htmlToPlainText(value).replace(/\s+/g, ' ').trim();

const RubricOutcomesPopup = ({
  assignmentId,
  hideAssignmentStatusPopup,
  studentResults = [],
}) => {
  const { data: allSubmissions, isLoadingdata: isLoadingAllSubmissions } =
    useAssignmentStatusById(assignmentId);

  const { data: assignmentData, isLoadingdata: isLoadingAssignmentData } =
    useAssignmentById(assignmentId);

  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const getResultsArray = () => {
    let resultsArray = allSubmissions || studentResults;

    if (studentResults?.stats && Array.isArray(studentResults.stats)) {
      resultsArray = studentResults.stats;
    } else if (allSubmissions?.stats && Array.isArray(allSubmissions.stats)) {
      resultsArray = allSubmissions.stats;
    }

    return resultsArray;
  };

  const isValidResult = (result) => {
    return result && result.studentName && result.questionSerialNumber?.value;
  };

  const initializeQuestionData = (
    question,
    title,
    questionMap,
    questionTitles,
    criteriaMap
  ) => {
    if (!questionMap[question]) {
      questionMap[question] = {};
      questionTitles[question] = title;
      criteriaMap[question] = [];
    }
  };

  const processCriteria = (question, criteriaWithLevels, criteriaMap) => {
    (criteriaWithLevels || []).forEach((criteria) => {
      if (!criteriaMap[question].includes(criteria.criteriaTitle)) {
        criteriaMap[question].push(criteria.criteriaTitle);
      }
    });
  };

  const processStudentResults = () => {
    const resultsArray = getResultsArray();

    if (!Array.isArray(resultsArray) || resultsArray.length === 0) {
      return {
        questions: [],
        students: [],
        questionMap: {},
        questionTitles: {},
        criteriaMap: {},
      };
    }

    const studentSet = new Set();
    const questionMap = {};
    const questionTitles = {};
    const criteriaMap = {};

    resultsArray.forEach((result) => {
      if (!isValidResult(result)) return;

      const {
        studentName,
        questionSerialNumber,
        questionTitle,
        criteriaWithLevels,
      } = result;
      const student = studentName;
      const question = questionSerialNumber.value;
      const title = toPlainTitle(questionTitle) || `Question ${question}`;

      studentSet.add(student);
      initializeQuestionData(
        question,
        title,
        questionMap,
        questionTitles,
        criteriaMap
      );
      processCriteria(question, criteriaWithLevels, criteriaMap);
      questionMap[question][student] = criteriaWithLevels;
    });

    const students = Array.from(studentSet);
    const questions = Object.keys(questionMap).sort((a, b) => +a - +b);

    return { questions, students, questionMap, questionTitles, criteriaMap };
  };

  const getAssignmentQuestions = () => {
    if (!assignmentData) return [];

    const questions = assignmentData.questions ||
      assignmentData.data?.questions ||
      assignmentData.assignment?.questions ||
      [];

    return questions;
  };

  const createQuestionData = (question, questionNumber, rubricQuestions) => {
    const questionTitle =
      toPlainTitle(question?.question) || `Question ${questionNumber}`;

    return {
      questionNumber,
      questionTitle,
      hasRubrics: rubricQuestions.includes(questionNumber.toString()),
      questionData: question,
    };
  };

  const getAllQuestions = () => {
    const assignmentQuestions = getAssignmentQuestions();
    const { questions: rubricQuestions, questionTitles: rubricTitles } =
      processStudentResults();

    const questionMap = {};
    const allQuestions = [];
    assignmentQuestions.forEach((question, index) => {
      const questionNumber = index + 1;
      const questionData = createQuestionData(
        question,
        questionNumber,
        rubricQuestions
      );

      allQuestions.push(questionData);
      questionMap[questionNumber] = questionData;
    });
    
    rubricQuestions.forEach((questionNum) => {
      const questionNumber = parseInt(questionNum);
      if (!questionMap[questionNumber]) {
        const questionData = {
          questionNumber,
          questionTitle:
            rubricTitles[questionNum] || `Question ${questionNumber}`,
          hasRubrics: true,
          questionData: null,
        };

        allQuestions.push(questionData);
        questionMap[questionNumber] = questionData;
      }
    });

    return allQuestions.sort((a, b) => a.questionNumber - b.questionNumber);
  };

  const getAssignmentTitle = () => {
    return (
      assignmentData?.title ||
      assignmentData?.assignment?.title ||
      'Untitled Assignment'
    );
  };

  const getCurrentQuestionTitle = (
    questionNumber,
    questionTitles,
    allQuestions
  ) => {
    const rubricTitle = questionTitles[questionNumber];

    const questionFromArray = allQuestions.find((q) => q.questionNumber === parseInt(questionNumber));
    
    const finalTitle = rubricTitle || questionFromArray?.questionTitle || `Question ${questionNumber}`;
    return finalTitle;
  };

  const createCriteriaLevelMap = (studentCriteria) => {
    const criteriaLevelMap = {};
    studentCriteria.forEach((criteria) => {
      criteriaLevelMap[criteria.criteriaTitle] = criteria.selectedLevel?.value;
    });
    return criteriaLevelMap;
  };

  const handlePreviousQuestion = () => {
    setCurrentQuestionIndex((prev) => Math.max(0, prev - 1));
  };

  const handleNextQuestion = (maxQuestions) => {
    setCurrentQuestionIndex((prev) => Math.min(maxQuestions - 1, prev + 1));
  };

  const renderQuestionNavigation = (totalQuestions) => (
    <QuestionNavigation>
      <QuestionBtn
        onClick={handlePreviousQuestion}
        disabled={currentQuestionIndex === 0}
      >
        <img src={LeftIcon} alt="Previous" />
        Previous
      </QuestionBtn>

      <QuestionCounter>
        <QuestionBox>
          Question {currentQuestionIndex + 1} of {totalQuestions}
        </QuestionBox>
      </QuestionCounter>

      <QuestionBtn
        onClick={() => handleNextQuestion(totalQuestions)}
        disabled={currentQuestionIndex === totalQuestions - 1}
      >
        Next
        <img src={RightIcon} alt="Next" />
      </QuestionBtn>
    </QuestionNavigation>
  );

  // `title` is already plain text: question titles are converted once where
  // they are read (toPlainTitle), and the task title is plain. Parsing it as
  // HTML again would drop literal text such as "<input>" (JED-1282).
  const renderQuestionTitle = (title) => (
    <QuestionTitleSection>
      <Tooltip title={title} arrow placement="top">
        <TruncatedText>{title}</TruncatedText>
      </Tooltip>
    </QuestionTitleSection>
  );

  const renderGridHeader = (criteria) => (
    <GridHeader>
      <HeaderCell>Student Name</HeaderCell>
      {criteria.map((criteriaTitle, idx) => (
        <HeaderCell key={idx} className="centered">
          <Tooltip title={criteriaTitle} arrow placement="top">
            <CriteriaTextContainer>
              <TruncatedCriteriaText>{criteriaTitle}</TruncatedCriteriaText>
            </CriteriaTextContainer>
          </Tooltip>
        </HeaderCell>
      ))}
    </GridHeader>
  );

  const renderStudentRows = (
    students,
    questionNumber,
    criteria,
    questionMap
  ) => {
    return students.map((student, idx) => {
      const studentCriteria = questionMap[questionNumber]?.[student] || [];
      const criteriaLevelMap = createCriteriaLevelMap(studentCriteria);

      return (
        <StudentRow key={idx}>
          <StudentCell>{student}</StudentCell>
          {criteria.map((criteriaTitle, criteriaIdx) => (
            <LevelCell key={criteriaIdx}>
              {criteriaLevelMap[criteriaTitle] ? (
                <LevelItem>{criteriaLevelMap[criteriaTitle]}</LevelItem>
              ) : (
                <NotAttempted>Not Attempted</NotAttempted>
              )}
            </LevelCell>
          ))}
        </StudentRow>
      );
    });
  };

  const renderNoRubricsContent = (totalQuestions, currentQuestionData) => {
    const questionTitle = currentQuestionData?.questionTitle || getAssignmentTitle();
    
    return (
      <>
        {renderQuestionNavigation(totalQuestions)}
        {renderQuestionTitle(questionTitle)}
        <NoRubricsMessage>
          No rubric outcomes available for this question.
        </NoRubricsMessage>
        <ViewSubmissionsButton assignmentdata={assignmentData} />
      </>
    );
  };

  const renderRubricsContent = (
    currentQuestionData,
    allQuestions,
    processedData
  ) => {
    const currentQuestionNumber = currentQuestionData.questionNumber.toString();

    const currentTitle = currentQuestionData.questionTitle;    
    const currentCriteria =
      processedData.criteriaMap[currentQuestionNumber] || [];
    
    const hasAnyRubricLevel = processedData.students.some((student) => {
    const studentCriteria =
        processedData.questionMap[currentQuestionNumber]?.[student] || [];
      return studentCriteria.some((c) => c.selectedLevel?.value);
    });

    if (!hasAnyRubricLevel) {
      return renderNoRubricsContent(allQuestions.length, currentQuestionData);
    }


    return (
      <>
        {renderQuestionNavigation(allQuestions.length)}
        {renderQuestionTitle(currentTitle)}
        <StudentsGridContainer>
          <StudentsGrid>
            {renderGridHeader(currentCriteria)}
            {renderStudentRows(
              processedData.students,
              currentQuestionNumber,
              currentCriteria,
              processedData.questionMap
            )}
          </StudentsGrid>
        </StudentsGridContainer>
        <ViewSubmissionsButton assignmentdata={assignmentData} />
      </>
    );
  };

  const renderQuestionContent = () => {
    const allQuestions = getAllQuestions();

    if (allQuestions.length === 0) {
      return (
        <NoResultsMessage>
          No questions available for this assignment.
        </NoResultsMessage>
      );
    }

    const currentQuestionData = allQuestions[currentQuestionIndex];

    return (
      <QuestionContainer>
        {currentQuestionData.hasRubrics
          ? renderRubricsContent(
              currentQuestionData,
              allQuestions,
              processStudentResults()
            )
          : renderNoRubricsContent(allQuestions.length, currentQuestionData)}
      </QuestionContainer>
    );
  };

  const renderContent = () => {
    const isLoading = isLoadingAllSubmissions || isLoadingAssignmentData;

    if (isLoading) return <Loader />;
    return renderQuestionContent();
  };

  return (
    <GeneralPopup
      hidePopup={hideAssignmentStatusPopup}
      title="Rubric Outcomes"
      textContent={
        <AssignmentStatusContainer>{renderContent()}</AssignmentStatusContainer>
      }
      showCloseIcon={true} 
      closeIcon={CloseIcon}
      cancelPopup={hideAssignmentStatusPopup}
      icon={RubricIcon}
      width="800px"
    />
  );
};

export default RubricOutcomesPopup;

const AssignmentStatusContainer = styled.div`
  width: 100%;
  font-family: var(--font-family-ibm_plex_sans);
`;

const NoRubricsMessage = styled.div`
  color: #50009d;
  padding: 12px 5px;
  border-radius: 8px;
  margin: 6px;
  font-family: var(--font-family-ibm_plex_sans);
  text-align: center;
  font-size: 16px;
`;

const NoResultsMessage = styled.div`
  text-align: center;
  padding: 40px 20px;
  font-size: 16px;
  font-style: italic;
  color: #999;
`;

const QuestionContainer = styled.div`
  margin-top: 20px;
`;

const QuestionNavigation = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const QuestionTitleSection = styled.div`
  display: flex;
  gap: 8px;
  flex: 1;
  align-items: center;
  justify-content: center;
  margin: 0 20px;
`;

const TruncatedText = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: #333;
  text-align: center;
  width: 60%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;
  line-height: 1.4;
`;

const StudentsGridContainer = styled.div`
  margin-top: 20px;
  width: 100%;
  border-radius: 12px;
  border: 1px solid #e4e4e4;
  overflow-x: auto;
  background: white;

  &::-webkit-scrollbar {
    height: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
`;
const StudentsGrid = styled.div`
  display: table;
  width: max-content;
  min-width: 100%;
`;

const GridHeader = styled.div`
  display: table-row;
  position: sticky;
  top: 0;
  z-index: 1;
  background-color: #f8f9fa;
`;

const HeaderCell = styled.div`
  display: table-cell;
  min-width: 100px;
  padding: 16px 12px;
  font-weight: 600;
  font-size: 14px;
  color: #ffffff;
  text-align: center;
  vertical-align: middle;
  background: #301b72;
  white-space: nowrap;

  &:first-child {
    min-width: 100px;
    text-align: left;
    padding-left: 16px;
    border-top-left-radius: 12px;
  }

  &:last-child {
    border-top-right-radius: 12px;
  }
`;

const CriteriaTextContainer = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const TruncatedCriteriaText = styled.div`
  font-size: 13px;
  line-height: 1.3;
  text-align: center;
  width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;
  max-width: 150px;
`;

const StudentRow = styled.div`
  display: table-row;
  width: 100%;
  &:hover {
    background-color: #f9f9f9;
  }
  
  &:hover > div {
    background-color: #f9f9f9;
  }
`;

const StudentCell = styled.div`
  display: table-cell;
  min-width: 100px;
  padding: 12px 16px;
  font-size: 14px;
  color: #301b72;
  font-weight: 500;
  border-bottom: 1px solid #efefef;
  vertical-align: middle;
  text-align: left;
  white-space: nowrap;
`;

const LevelCell = styled.div`
  display: table-cell;
  min-width: 100px;
  max-width: 150px;
  padding: 12px 12px;
  border-bottom: 1px solid #efefef;
  vertical-align: middle;
  text-align: center;
  width: auto;
`;

const LevelItem = styled.div`
  padding: 4px 8px;
  border-radius: 4px;
  text-align: center;
  font-size: 13px;
  font-weight: 600;
  word-wrap: break-word;
  word-break: break-word;
  white-space: normal;
  max-width: 100%;
  width: 100%;
  line-height: 1.2;
  hyphens: auto;
  overflow-wrap: break-word;
  display: block;
`;

const NotAttempted = styled.div`
  font-size: 13px;
  color: #999;
  font-style: italic;
  text-align: center;
`;
